import { useMemo, useState } from 'react';
import { Badge, Button, Card, Select } from '@nexora/design-system';
import { useTaxRates, type TaxRate } from '../tax-rates/useTaxRates';
import { useTaxProfiles, isMappedForReform, type TaxProfile } from '../tax-profiles/useTaxProfiles';
import { useFiscalInvoices, type Invoice } from '../useFiscalInvoices';
import { useServices } from '../../master-data/useMasterData';
import { useAuth } from '../../../shared/auth/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import { formatCurrency, formatDate } from '../../../shared/utils/format';

/**
 * Painel de Transição da Reforma Tributária.
 *
 * A versão anterior era um texto explicativo com uma tabela de alíquotas
 * **escrita à mão** ("IBS: 0,1%", "9,5%"). O número que vale é o que está
 * cadastrado no tenant e o que o motor aplica: `TaxRateEntity` guarda a
 * alíquota de IBS e CBS, e `TaxCalculationContext.reformaTransitionPercent`
 * define o percentual de transição de cada ano. Esta tela lê os dois.
 *
 * O que não foi adotado da referência, por falta de fonte, está no fim do
 * arquivo em `NAO_ADOTADO`.
 */

/** `TaxCalculationContext.reformaTransitionPercent`, ano a ano. */
const TRANSICAO: Record<number, number> = {
  2026: 0.01,
  2027: 0.1,
  2028: 0.2,
  2029: 0.4,
  2030: 0.6,
  2031: 0.8,
  2032: 0.9,
};

const ANOS = [2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033];

function percentualDaTransicao(ano: number): number {
  if (ano < 2026) return 0;
  return TRANSICAO[ano] ?? 1;
}

/** Alíquota vigente na data de hoje, entre as cadastradas para o tributo. */
function aliquotaVigente(rates: TaxRate[], taxType: string): TaxRate | null {
  const hoje = new Date();
  const candidatas = rates
    .filter((r) => r.taxType === taxType)
    .filter((r) => {
      const desde = new Date(r.effectiveSince);
      if (!Number.isNaN(desde.getTime()) && desde > hoje) return false;
      if (!r.effectiveUntil) return true;
      const ate = new Date(r.effectiveUntil);
      return Number.isNaN(ate.getTime()) || ate >= hoje;
    })
    .sort((a, b) => b.rulePriority - a.rulePriority);
  return candidatas[0] ?? null;
}

/** Itens de master data que precisam de classificação IBS/CBS. */
interface ItemMapeavel {
  id: string;
  nome: string;
  codigo: string;
  tipo: 'Produto' | 'Serviço';
  perfil: TaxProfile | null;
}

function classificacaoDe(item: ItemMapeavel): string | null {
  return item.perfil && isMappedForReform(item.perfil) ? item.perfil.classificacaoTributaria : null;
}

/** NCMs sem classificação IBS/CBS que aparecem num documento. */
function ncmsSemClassificacao(invoice: Invoice, ncmsMapeados: ReadonlySet<string>): string[] {
  const faltando = new Set<string>();
  for (const item of invoice.items) {
    const ncm = (item.ncm ?? '').replace(/\D/g, '');
    if (!ncm) continue;
    if (!ncmsMapeados.has(ncm)) faltando.add(ncm);
  }
  return [...faltando];
}

export function TaxReformPage(): JSX.Element {
  const { tenant } = useAuth();
  const [anoFoco, setAnoFoco] = useState<number>(() => {
    const atual = new Date().getFullYear();
    return atual < 2026 ? 2026 : Math.min(atual, 2033);
  });

  const ratesQ = useTaxRates({ limit: 200 });
  const perfisQ = useTaxProfiles();
  /* Mesmo padrao das telas de estoque: `GET /products` com o minimo lido. */
  const produtosQ = useQuery({
    queryKey: ['products', 'reforma'],
    queryFn: () => apiClient.get<{ id: string; name: string; sku?: string }[]>('/products'),
    staleTime: 5 * 60 * 1000,
  });
  const servicosQ = useServices();
  const notasQ = useFiscalInvoices(tenant?.id ?? '', { limit: 100 });

  const rates = ratesQ.data?.data ?? [];
  /* `?? []` cria array novo a cada render e invalidaria os `useMemo` abaixo. */
  const perfis = useMemo(() => perfisQ.data ?? [], [perfisQ.data]);

  const ibs = aliquotaVigente(rates, 'IBS');
  const cbs = aliquotaVigente(rates, 'CBS');

  /* Sem alíquota cadastrada o motor simplesmente NÃO aplica IBS/CBS: o
     `CalculateTaxesUseCase` engole a falta num `catch` vazio e o documento sai
     sem os dois tributos, sem erro. É a primeira coisa que este painel precisa
     dizer. */
  const semAliquota = !ratesQ.isLoading && (!ibs || !cbs);

  const fator = percentualDaTransicao(anoFoco);
  const ibsEfetiva = ibs ? ibs.aliquota * fator : null;
  const cbsEfetiva = cbs ? cbs.aliquota * fator : null;

  /* ── Mapeamento de NCMs e serviços ── */
  const perfilPorProduto = useMemo(() => {
    const mapa = new Map<string, TaxProfile>();
    for (const p of perfis) if (p.productId) mapa.set(p.productId, p);
    return mapa;
  }, [perfis]);

  const perfilPorServico = useMemo(() => {
    const mapa = new Map<string, TaxProfile>();
    for (const p of perfis) if (p.serviceId) mapa.set(p.serviceId, p);
    return mapa;
  }, [perfis]);

  const itens = useMemo<ItemMapeavel[]>(() => {
    const lista: ItemMapeavel[] = [];
    for (const p of produtosQ.data ?? []) {
      lista.push({
        id: p.id,
        nome: p.name,
        codigo: p.sku ?? '',
        tipo: 'Produto',
        perfil: perfilPorProduto.get(p.id) ?? null,
      });
    }
    for (const s of servicosQ.data ?? []) {
      lista.push({
        id: s.id,
        nome: s.name,
        codigo: s.code,
        tipo: 'Serviço',
        perfil: perfilPorServico.get(s.id) ?? null,
      });
    }
    return lista;
  }, [produtosQ.data, servicosQ.data, perfilPorProduto, perfilPorServico]);

  const mapeados = itens.filter((i) => classificacaoDe(i) !== null);
  const pendentes = itens.filter((i) => classificacaoDe(i) === null);
  const percentual = itens.length === 0 ? null : (mapeados.length / itens.length) * 100;

  /** NCMs com perfil classificado — usados para avaliar os documentos. */
  const ncmsMapeados = useMemo(() => {
    const set = new Set<string>();
    for (const p of perfis) {
      if (!isMappedForReform(p)) continue;
      if (p.ncmCode) set.add(p.ncmCode.replace(/\D/g, ''));
    }
    for (const item of itens) {
      const perfil = item.perfil;
      if (perfil && isMappedForReform(perfil) && perfil.ncmCode) {
        set.add(perfil.ncmCode.replace(/\D/g, ''));
      }
    }
    return set;
  }, [perfis, itens]);

  /* ── Documentos no período de dupla incidência ── */
  const notasDaTransicao = useMemo(() => {
    const notas = notasQ.data?.data ?? [];
    return notas
      .filter((n) => {
        const ano = Number((n.emissionDate ?? '').slice(0, 4));
        return Number.isFinite(ano) && ano >= 2026;
      })
      .map((n) => ({ nota: n, faltando: ncmsSemClassificacao(n, ncmsMapeados) }))
      .sort((a, b) => b.faltando.length - a.faltando.length);
  }, [notasQ.data, ncmsMapeados]);

  const notasComPendencia = notasDaTransicao.filter((n) => n.faltando.length > 0);

  const exportarCsv = (): void => {
    const linhas = [
      ['Tipo', 'Código', 'Nome', 'Classificação IBS/CBS', 'CST'],
      ...itens.map((i) => [
        i.tipo,
        i.codigo,
        i.nome,
        i.perfil?.classificacaoTributaria ?? '',
        i.perfil?.cstIbsCbs ?? '',
      ]),
    ];
    const csv = linhas
      .map((l) => l.map((c) => `"${c.replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const url = URL.createObjectURL(
      new Blob([BOM_UTF8 + csv], { type: 'text/csv;charset=utf-8' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `mapeamento-ibs-cbs-${anoFoco}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const carregando =
    ratesQ.isLoading || perfisQ.isLoading || produtosQ.isLoading || servicosQ.isLoading;

  return (
    <div className="rf-page">
      <div className="rf-header">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Painel de Transição</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Reforma Tributária — o que está cadastrado, o que falta classificar e
            os documentos já emitidos na dupla incidência.
          </p>
        </div>
        <Button variant="secondary" onClick={exportarCsv} disabled={itens.length === 0}>
          Exportar mapeamento (CSV)
        </Button>
      </div>

      {/* ── Alíquota não cadastrada: o motor cala e o documento sai sem IBS/CBS ── */}
      {semAliquota && (
        <Card>
          <div className="rf-card rf-alert">
            <Badge variant="warning" size="sm" dot>
              atenção
            </Badge>
            <p>
              {!ibs && !cbs
                ? 'Não há alíquota de IBS nem de CBS em vigor no cadastro.'
                : !ibs
                  ? 'Não há alíquota de IBS em vigor no cadastro.'
                  : 'Não há alíquota de CBS em vigor no cadastro.'}{' '}
              O motor fiscal não aplica os dois tributos quando a alíquota não
              existe — e não devolve erro: o documento sai sem IBS/CBS. Cadastre
              em <strong>Configuração de Tributos</strong>.
            </p>
          </div>
        </Card>
      )}

      {/* ── Indicadores ── */}
      <div className="rf-kpis">
        <Card>
          <div className="rf-kpi">
            <span className="rf-kpi__label">Alíquota efetiva em {anoFoco}</span>
            <strong className="rf-kpi__value">
              {carregando
                ? '…'
                : ibsEfetiva !== null && cbsEfetiva !== null
                  ? `${(ibsEfetiva + cbsEfetiva).toFixed(2).replace('.', ',')}%`
                  : '—'}
            </strong>
            <span className="rf-kpi__foot">
              {ibsEfetiva !== null && cbsEfetiva !== null
                ? `IBS ${ibsEfetiva.toFixed(2).replace('.', ',')}% + CBS ${cbsEfetiva.toFixed(2).replace('.', ',')}%`
                : 'alíquota não cadastrada'}
            </span>
            <span className="rf-kpi__foot">
              {(fator * 100).toFixed(0)}% da alíquota cheia (escada da transição)
            </span>
          </div>
        </Card>
        <Card>
          <div className="rf-kpi">
            <span className="rf-kpi__label">Mapeamento IBS/CBS</span>
            <strong className="rf-kpi__value">
              {carregando ? '…' : percentual === null ? '—' : `${percentual.toFixed(0)}%`}
            </strong>
            <span className="rf-kpi__foot">
              {mapeados.length} de {itens.length} itens com CST e classificação
            </span>
            <div className="rf-bar">
              <div className="rf-bar__fill" style={{ width: `${percentual ?? 0}%` }} />
            </div>
          </div>
        </Card>
        <Card>
          <div className="rf-kpi">
            <span className="rf-kpi__label">Documentos na dupla incidência</span>
            <strong className="rf-kpi__value">
              {notasQ.isLoading ? '…' : notasDaTransicao.length}
            </strong>
            <span className="rf-kpi__foot">
              emitidos de 2026 em diante, entre os {notasQ.data?.data.length ?? 0} carregados
            </span>
            <span className={notasComPendencia.length > 0 ? 'rf-warn' : 'rf-ok'}>
              {notasComPendencia.length > 0
                ? `${notasComPendencia.length} com NCM sem classificação`
                : 'nenhum com NCM sem classificação'}
            </span>
          </div>
        </Card>
      </div>

      {/* ── Escada da transição ── */}
      <Card>
        <div className="rf-card">
          <div className="rf-section-head">
            <h3 className="rf-section">Escada da transição</h3>
            <div className="rf-year">
              <Select
                label="Ano de referência"
                value={String(anoFoco)}
                onChange={(e) => setAnoFoco(Number(e.target.value))}
              >
                {ANOS.map((ano) => (
                  <option key={ano} value={ano}>
                    {ano}
                    {ano === 2033 ? '+' : ''}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <p className="rf-hint">
            O percentual de cada ano é o do próprio motor
            (<code>reformaTransitionPercent</code>); a alíquota cheia é a que está
            cadastrada e em vigor. A coluna de valor fica vazia quando não há
            alíquota cadastrada — não há número a exibir.
          </p>

          <div className="rf-table-wrap">
            <table className="rf-table">
              <thead>
                <tr>
                  <th>Ano</th>
                  <th className="rf-num">% da alíquota cheia</th>
                  <th className="rf-num">IBS efetivo</th>
                  <th className="rf-num">CBS efetivo</th>
                  <th className="rf-num">Soma</th>
                  <th>Convivência com ICMS/ISS</th>
                </tr>
              </thead>
              <tbody>
                {ANOS.map((ano) => {
                  const f = percentualDaTransicao(ano);
                  const i = ibs ? ibs.aliquota * f : null;
                  const c = cbs ? cbs.aliquota * f : null;
                  return (
                    <tr key={ano} className={ano === anoFoco ? 'rf-row--foco' : undefined}>
                      <td className="rf-mono">
                        {ano}
                        {ano === 2033 ? '+' : ''}
                      </td>
                      <td className="rf-num">{(f * 100).toFixed(0)}%</td>
                      <td className="rf-num">
                        {i === null ? '—' : `${i.toFixed(2).replace('.', ',')}%`}
                      </td>
                      <td className="rf-num">
                        {c === null ? '—' : `${c.toFixed(2).replace('.', ',')}%`}
                      </td>
                      <td className="rf-num">
                        {i === null || c === null
                          ? '—'
                          : `${(i + c).toFixed(2).replace('.', ',')}%`}
                      </td>
                      <td>
                        {ano >= 2033 ? (
                          <Badge variant="neutral" size="sm">
                            substituídos
                          </Badge>
                        ) : (
                          <Badge variant="info" size="sm">
                            dupla incidência
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="rf-hint">
            Alíquota cheia em vigor:{' '}
            {ibs ? `IBS ${ibs.aliquota.toFixed(2).replace('.', ',')}%` : 'IBS não cadastrado'} ·{' '}
            {cbs ? `CBS ${cbs.aliquota.toFixed(2).replace('.', ',')}%` : 'CBS não cadastrado'}
            {ibs ? ` · vigente desde ${formatDate(ibs.effectiveSince)}` : ''}
          </p>
        </div>
      </Card>

      {/* ── Mapeamento pendente ── */}
      <Card>
        <div className="rf-card">
          <h3 className="rf-section">Itens sem classificação IBS/CBS</h3>
          <p className="rf-hint">
            Um item está classificado quando o perfil fiscal tem <strong>CST</strong> e{' '}
            <strong>classificação tributária</strong> — sem os dois o motor não sabe
            tratar o item no novo modelo.
          </p>
          {carregando ? (
            <p className="rf-muted">carregando…</p>
          ) : pendentes.length === 0 ? (
            <p className="rf-ok">
              Todos os {itens.length} produtos e serviços estão classificados.
            </p>
          ) : (
            <div className="rf-table-wrap">
              <table className="rf-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Código</th>
                    <th>Nome</th>
                    <th>NCM / NBS do perfil</th>
                    <th>Falta</th>
                  </tr>
                </thead>
                <tbody>
                  {pendentes.slice(0, 20).map((item) => (
                    <tr key={`${item.tipo}-${item.id}`}>
                      <td>
                        <Badge variant="neutral" size="sm">
                          {item.tipo}
                        </Badge>
                      </td>
                      <td className="rf-mono">{item.codigo || '—'}</td>
                      <td>{item.nome}</td>
                      <td className="rf-mono">
                        {item.perfil?.ncmCode ?? item.perfil?.nbsCode ?? '—'}
                      </td>
                      <td>
                        {!item.perfil ? (
                          <Badge variant="warning" size="sm">
                            sem perfil fiscal
                          </Badge>
                        ) : (
                          <span className="rf-falta rf-falta--row">
                            {!item.perfil.cstIbsCbs ? (
                              <Badge variant="warning" size="sm">
                                CST
                              </Badge>
                            ) : null}
                            {!item.perfil.classificacaoTributaria ? (
                              <Badge variant="warning" size="sm">
                                classificação
                              </Badge>
                            ) : null}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pendentes.length > 20 && (
                <p className="rf-hint">
                  e mais {pendentes.length - 20} — a lista completa está no CSV.
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* ── Documentos já emitidos na janela ── */}
      <Card>
        <div className="rf-card">
          <h3 className="rf-section">Documentos emitidos na dupla incidência</h3>
          <p className="rf-hint">
            Notas emitidas de 2026 em diante, com a situação do IBS/CBS avaliada
            pelo NCM de cada item contra os perfis classificados.
          </p>
          {notasQ.isLoading ? (
            <p className="rf-muted">carregando…</p>
          ) : notasDaTransicao.length === 0 ? (
            <p className="rf-muted">
              Nenhum documento emitido de 2026 em diante entre os carregados.
            </p>
          ) : (
            <div className="rf-table-wrap">
              <table className="rf-table">
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Emissão</th>
                    <th className="rf-num">Valor</th>
                    <th>Situação IBS/CBS</th>
                  </tr>
                </thead>
                <tbody>
                  {notasDaTransicao.slice(0, 15).map(({ nota, faltando }) => (
                    <tr key={nota.id}>
                      <td className="rf-mono">
                        {nota.type}
                        <small className="rf-sub">
                          {/* `accessKey` e nulo antes da autorizacao. */}
                          {nota.accessKey ? nota.accessKey.slice(-12) : nota.id.slice(0, 12)}
                        </small>
                      </td>
                      <td className="rf-mono">{formatDate(nota.emissionDate)}</td>
                      <td className="rf-num">{formatCurrency(nota.totals.totalValue ?? 0)}</td>
                      <td>
                        {faltando.length === 0 ? (
                          <Badge variant="success" size="sm" dot>
                            itens classificados
                          </Badge>
                        ) : (
                          <span className="rf-falta">
                            <Badge variant="warning" size="sm" dot>
                              {faltando.length} NCM sem classificação
                            </Badge>
                            <small className="rf-sub">{faltando.slice(0, 3).join(', ')}</small>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* ── O que a referência pede e não tem fonte ── */}
      <Card>
        <div className="rf-card">
          <h3 className="rf-section">Fora deste painel</h3>
          <ul className="rf-list">
            {NAO_ADOTADO.map((item) => (
              <li key={item.titulo}>
                <strong>{item.titulo}</strong> — {item.motivo}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <style>{`
        .rf-page { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
        .rf-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 8px; }
        .rf-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
        .rf-alert { flex-direction: row; align-items: flex-start; gap: 8px; }
        .rf-alert p { margin: 0; font-size: 12px; line-height: 18px; color: #633f0f; }
        .rf-section-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
        .rf-section { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; color: #191c1e; margin: 0; }
        .rf-year { min-width: 160px; }
        .rf-hint { font-size: 12px; line-height: 16px; color: #43474e; margin: 0; }
        .rf-muted { font-size: 12px; color: #74777f; margin: 0; }
        .rf-ok { font-size: 12px; color: #1a6b33; margin: 0; }
        .rf-warn { font-size: 12px; color: #633f0f; }
        .rf-kpis { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
        @media (max-width: 900px) { .rf-kpis { grid-template-columns: 1fr; } }
        .rf-kpi { padding: 16px; display: flex; flex-direction: column; gap: 4px; }
        .rf-kpi__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em; color: #43474e; }
        .rf-kpi__value { font-family: Inter, sans-serif; font-size: 24px; line-height: 32px; font-weight: 600; color: #191c1e; font-variant-numeric: tabular-nums; }
        .rf-kpi__foot { font-size: 12px; color: #74777f; font-variant-numeric: tabular-nums; }
        .rf-bar { height: 6px; border-radius: 3px; background: #e0e3e5; margin-top: 4px; overflow: hidden; }
        .rf-bar__fill { height: 100%; background: #002045; border-radius: 3px; }
        .rf-table-wrap { overflow-x: auto; }
        .rf-table { width: 100%; border-collapse: collapse; }
        .rf-table th { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase; color: #43474e; text-align: left; padding: 8px 12px; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .rf-table td { padding: 8px 12px; border-bottom: 1px solid #eceef0; font-size: 12px; color: #191c1e; vertical-align: middle; }
        .rf-row--foco td { background: #f2f4f6; font-weight: 600; }
        .rf-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .rf-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; white-space: nowrap; }
        .rf-sub { display: block; color: #74777f; font-size: 12px; font-weight: 400; }
        .rf-falta { display: inline-flex; flex-direction: column; gap: 2px; }
        .rf-falta > span { font-size: 12px; color: #633f0f; }
        .rf-falta--row { flex-direction: row; flex-wrap: wrap; gap: 4px; }
        .rf-list { margin: 0; padding-left: 18px; font-size: 12px; line-height: 20px; color: #43474e; }
      `}</style>
    </div>
  );
}

/** Sem o BOM, o Excel em pt-BR abre o CSV como ANSI e quebra os acentos. */
const BOM_UTF8 = String.fromCharCode(0xfeff);

/**
 * O que a referência (`erpone-painel-de-transi-o-reforma-tribut-ria`) pede e
 * que este painel não mostra, com o motivo. Fica na tela, não só no commit:
 * quem abre o painel precisa saber o que ele não responde.
 */
const NAO_ADOTADO: { titulo: string; motivo: string }[] = [
  {
    titulo: 'Comparativo de carga em reais (modelo atual × IBS/CBS)',
    motivo:
      'não há apuração agregada por modelo no backend. O comparativo item a item existe no Simulador de Impacto, que roda o motor de verdade sobre os itens informados.',
  },
  {
    titulo: 'Crédito acumulado de IBS/CBS',
    motivo: 'não existe conta de crédito de IBS/CBS no domínio — nada apura nem guarda esse saldo.',
  },
  {
    titulo: 'Atualizações legais (feed SEFAZ / Receita / Comitê Gestor)',
    motivo: 'é conteúdo externo; nenhuma integração publica esse feed.',
  },
  {
    titulo: 'Sincronizar ERP',
    motivo: 'não há endpoint de sincronização; as alíquotas e os perfis são cadastro do próprio sistema.',
  },
];
